import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import OrgReg from "./pages/OrgReg";
import OrgLogin from "./pages/OrgLogin";
import OrgVerifyOtp from "./pages/OrgVerifyOtp";
import NewLogin from "./pages/NewLogin";
import OrgDashboard from "./pages/OrgDashboard/OrgDashboard";
import Calculation from "./pages/Calculation/Calculation";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authReadyState, authState } from "./recoil/authAtom";
import { getSessionType } from "./utils/auth";
import UserDashboard from "./pages/UserDashboard/UserDashboard";
import ForgotPassword from "./pages/ForgotPassword";
import VerifyOtp from "./pages/VerifyOtp";
import useAutoLogout from "./hooks/useAutoLogout";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CentreLogin from "./pages/Center/CentreLogin";
import PatientForm from "./pages/Center/PatientForm";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Maintenance from "./pages/Maintenance";
import LandingPage from "./pages/Landing/LandingPage";
import Patient from "./components/PatientManagement/Patient";
import PasswordExpiryNotification from "./components/common/PasswordExpiryNotification";

const dashboardPathFor = (sessionType) => {
  if (sessionType === "org") return "/org-dashboard";
  if (sessionType === "centre") return "/centre/pat-form";
  if (sessionType === "doctor") return "/dashboard/prescription";
  if (sessionType === "user") return "/dashboard";
  return "/";
};

const ProtectedRoute = ({ children, sessionType, loginPath }) => {
  const auth = useRecoilValue(authState);
  const ready = useRecoilValue(authReadyState);
  if (!ready) return null;
  if (!auth) return <Navigate to={loginPath} replace />;
  const actualSessionType = getSessionType(auth);
  if (!actualSessionType) return <Navigate to={loginPath} replace />;
  return (
    Array.isArray(sessionType)
      ? sessionType.includes(actualSessionType)
      : actualSessionType === sessionType
  ) ? (
    children
  ) : (
    <Navigate to={dashboardPathFor(actualSessionType)} replace />
  );
};

const SessionExpiryRedirect = () => {
  const navigate = useNavigate();
  const setAuth = useSetRecoilState(authState);

  useEffect(() => {
    const onSessionExpired = (event) => {
      const sessionType = event.detail?.sessionType;
      setAuth(null);
      navigate(
        sessionType === "org"
          ? "/org-login"
          : sessionType === "centre"
            ? "/centre/login"
            : "/user-login",
        { replace: true },
      );
    };
    window.addEventListener("litss:session-expired", onSessionExpired);
    return () =>
      window.removeEventListener("litss:session-expired", onSessionExpired);
  }, [navigate, setAuth]);

  return null;
};

function App() {
  return (
    <Router>
      <AppWithAutoLogout />
    </Router>
  );
}

// Wrap app inside a component with `useNavigate`
function AppWithAutoLogout() {
  const navigate = useNavigate();
  useAutoLogout(navigate); // Pass `navigate` to the auto-logout hook

  return (
    <>
      <SessionExpiryRedirect />
      <PasswordExpiryNotification />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/org-register" element={<OrgReg />} />
        <Route path="/org-verify-otp" element={<OrgVerifyOtp />} />
        <Route path="/org-login" element={<OrgLogin />} />
        <Route path="/user-login" element={<NewLogin />} />
        <Route path="/centre/login" element={<CentreLogin />} />
        <Route
          path="/centre/pat-form"
          element={
            <ProtectedRoute sessionType="centre" loginPath="/centre/login">
              <PatientForm />
            </ProtectedRoute>
          }
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/common/verify-otp" element={<VerifyOtp />} />
        <Route
          path="/org-dashboard"
          element={
            <ProtectedRoute sessionType="org" loginPath="/org-login">
              <OrgDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/calculation"
          element={
            <ProtectedRoute sessionType="org" loginPath="/org-login">
              <Calculation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/*"
          element={
            <ProtectedRoute
              sessionType={["user", "doctor"]}
              loginPath="/user-login"
            >
              <UserDashboard />
            </ProtectedRoute>
          }
        />
      </Routes>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
}

export default App;
